import type { ResolvedTerm } from "@/shared/utils/resolveTerm";

/**
 * What the tag lookup attached to each entry of a watchlist being built.
 *
 * Its own module, importing only types: these are the rules worth testing, and
 * the hook they serve reaches `window` on import.
 *
 * The lookup is right most of the time and confidently wrong the rest. A
 * pasted "William" leads the label "William Hill", so it comes back as the
 * ticker WMH.LN carrying William Hill's tag — a first name filed as a
 * bookmaker, with nothing on screen saying where WMH.LN came from. Keeping
 * what was typed is what lets the builder show that, and undo it without
 * losing the word.
 */

export interface TermMatch {
  /** Saved on the list's combined watchlist — see listStorage.ts. */
  tagId: string;
  /** The tag's name — "William Hill PLC". */
  label?: string;
  /**
   * What the user entered, when the lookup replaced it with a ticker —
   * "William" for WMH.LN. Only an entry carrying this is up for review, and
   * it is the word that "Keep as keyword" puts back.
   */
  typed?: string;
}

/** Keyed by the stored term: the ticker, or the keyword. */
export type TermMatches = Record<string, TermMatch>;

export interface TermLists {
  tickers: string[];
  keywords: string[];
  matches: TermMatches;
}

export interface TermEntry {
  term: string;
  kind: "ticker" | "keyword";
  match?: TermMatch;
}

/**
 * Whether what was typed is the symbol itself, in some venue's spelling.
 *
 * "AAPL.O" and "AAPL US Equity" both name AAPL, and "WMH" names WMH.LN — none
 * of them were read as anything, so none need checking. Case counts: "GOLD"
 * typed in capitals asks for the symbol, while "gold" and "Mar" are words the
 * lookup happened to find a ticker for, which is exactly what review is for.
 */
function isSymbolSpelling(typed: string, ticker: string): boolean {
  const extendsWith = (long: string, short: string) =>
    long === short ||
    (long.startsWith(short) && /[.\s]/.test(long.charAt(short.length)));
  return extendsWith(typed, ticker) || extendsWith(ticker, typed);
}

/** The entry a resolved term files, and the match it carries, if any. */
export function matchFromResolved(
  resolved: ResolvedTerm
): { term: string; match: TermMatch } | undefined {
  if (resolved.tagId == null) return undefined;
  const replaced =
    !!resolved.ticker && !isSymbolSpelling(resolved.input, resolved.ticker);
  return {
    term: resolved.ticker ?? resolved.keyword ?? resolved.input,
    match: {
      tagId: String(resolved.tagId),
      label: resolved.tagLabel,
      typed: replaced ? resolved.input : undefined,
    },
  };
}

/**
 * Records a match against its entry, deciding whether the entry is up for
 * review.
 *
 * Keeping a reviewed entry as a keyword takes its ticker out, so review is
 * only offered while the lookup is the sole reason the ticker is there:
 *
 *   - the symbol typed as itself beats an inference, in either order —
 *     "AAPL, Apple" leaves AAPL as a ticker the user asked for;
 *   - a ticker already listed with no match (typed before the lookup ran,
 *     imported, or on the list being edited) is the user's, and the lookup
 *     only adds its tag;
 *   - an entry that is already up for review stays up, whichever word found
 *     it again.
 */
export function recordMatch(
  matches: TermMatches,
  term: string,
  match: TermMatch,
  wasListed: boolean
): TermMatches {
  const prev = matches[term];
  const typed =
    match.typed === undefined
      ? undefined
      : prev
        ? prev.typed
        : wasListed
          ? undefined
          : match.typed;
  return { ...matches, [term]: { ...match, typed } };
}

export function withoutMatch(matches: TermMatches, term: string): TermMatches {
  if (!(term in matches)) return matches;
  const next = { ...matches };
  delete next[term];
  return next;
}

/**
 * The builder's entries in the order to read them: what to check, then the
 * rest by kind.
 *
 * Tickers the lookup put in place of a word come first — the word itself no
 * longer matches anything, so a wrong one quietly narrows the list. Words that
 * kept their text and gained a topic tag follow: a wrong one only widens it.
 * Paste order holds within each, so the rows line up with the source list.
 */
export function groupTerms(
  tickers: string[],
  keywords: string[],
  matches: TermMatches
): { toCheck: TermEntry[]; tickers: TermEntry[]; keywords: TermEntry[] } {
  const toCheck: TermEntry[] = [];
  const plainTickers: TermEntry[] = [];
  const plainKeywords: TermEntry[] = [];
  for (const term of tickers) {
    const match = matches[term];
    (match?.typed ? toCheck : plainTickers).push({ term, kind: "ticker", match });
  }
  for (const term of keywords) {
    const match = matches[term];
    (match ? toCheck : plainKeywords).push({ term, kind: "keyword", match });
  }
  return { toCheck, tickers: plainTickers, keywords: plainKeywords };
}

/**
 * Drops an entry's match and keeps the word.
 *
 * A ticker the lookup made from a word goes back to that word; one typed as a
 * symbol becomes a keyword as typed; a keyword just loses its topic tag. In
 * every case what is left is the user's text, matched as text.
 */
export function keepAsKeyword(lists: TermLists, term: string): TermLists {
  const match = lists.matches[term];
  if (!match) return lists;
  const matches = withoutMatch(lists.matches, term);
  if (!lists.tickers.includes(term)) return { ...lists, matches };

  const word = match.typed ?? term;
  return {
    tickers: lists.tickers.filter((t) => t !== term),
    keywords: lists.keywords.includes(word)
      ? lists.keywords
      : [...lists.keywords, word],
    matches,
  };
}

/**
 * The tags to save: one per matched entry still in the list.
 *
 * Read from the lists rather than from every match ever recorded, so an entry
 * removed after its match was found takes its tag with it. Tags widen what a
 * list matches, so one left behind would keep pulling in the headlines of
 * something the user deleted.
 */
export function tagsToSave(
  tickers: string[],
  keywords: string[],
  matches: TermMatches
): string[] {
  const ids = [...tickers, ...keywords]
    .map((term) => matches[term]?.tagId)
    .filter((id): id is string => !!id);
  return [...new Set(ids)];
}
